import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, ChevronRight, Rocket, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Owner-facing "Ready to launch?" checklist that ticks itself green as the
 * café completes setup. Reads state from
 *   GET /t/{slug}/admin/launch-checklist
 *
 * Once every item is done we auto-collapse into a subtle "You're all set"
 * strip so the dashboard isn't cluttered forever.
 *
 * Props:
 *   api  — admin axios instance (buildAdminApi(slug))
 *   slug — café slug (used to build CTA hrefs client-side)
 */
const LaunchChecklist = ({ api }) => {
    const [payload, setPayload] = useState(null); // {items, summary}
    const [expanded, setExpanded] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        let alive = true;
        api.get("/admin/launch-checklist")
            .then((r) => { if (alive) setPayload(r.data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [api]);

    if (!payload || dismissed) return null;
    const { items, summary } = payload;

    // Fully-set case — subtle collapsed strip; owner can dismiss for good.
    if (summary.all_done) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl bg-[#0E6B4C]/10 border border-[#0E6B4C]/30 p-4 flex items-center justify-between gap-3"
                data-testid="checklist-all-done"
            >
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#0E6B4C] flex items-center justify-center text-white">
                        <Rocket className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display font-black">You're launch-ready ☕️</div>
                        <div className="text-xs text-[#8C7A72]">All 6 setup items complete. Time to sell coffee.</div>
                    </div>
                </div>
                <button
                    onClick={() => setDismissed(true)}
                    data-testid="checklist-dismiss"
                    className="text-xs uppercase tracking-wider font-bold text-[#8C7A72] hover:text-[#2C1A12]"
                >
                    Dismiss
                </button>
            </motion.div>
        );
    }

    const pct = summary.percent;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-3xl bg-gradient-to-br from-[#2C1A12] to-[#3E2418] text-white p-6 relative overflow-hidden"
            data-testid="launch-checklist"
        >
            {/* Decorative glow */}
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#FF4F18]/25 blur-2xl" />
            <div className="absolute -left-10 -bottom-10 h-32 w-32 rounded-full bg-[#FFD166]/15 blur-2xl" />

            <div className="relative flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        Ready to launch?
                    </div>
                    <h2 className="mt-1 font-display font-black text-2xl">
                        {summary.done}/{summary.total} · {pct}% complete
                    </h2>
                </div>
                <button
                    onClick={() => setExpanded((x) => !x)}
                    data-testid="checklist-toggle"
                    className="text-xs uppercase tracking-wider font-bold text-white/70 hover:text-white"
                >
                    {expanded ? "Hide" : "Show list"}
                </button>
            </div>

            {/* Progress bar */}
            <div className="relative mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ type: "spring", stiffness: 100, damping: 20 }}
                    className="h-full rounded-full bg-gradient-to-r from-[#FF4F18] to-[#FFD166]"
                    data-testid="checklist-progress"
                />
            </div>

            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="items"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="relative mt-5 space-y-2 overflow-hidden"
                    >
                        {items.map((it) => (
                            <button
                                key={it.key}
                                onClick={() => {
                                    if (it.done) return;
                                    if (it.cta_href?.startsWith("/")) {
                                        nav(it.cta_href);
                                    } else if (it.cta_href) {
                                        window.location.href = it.cta_href;
                                    }
                                }}
                                data-testid={`checklist-item-${it.key}`}
                                className={`w-full text-left rounded-2xl p-3 flex items-center gap-3 transition-colors ${
                                    it.done
                                        ? "bg-white/5 cursor-default"
                                        : "bg-white/10 hover:bg-white/15 active:scale-[0.995]"
                                }`}
                            >
                                {it.done ? (
                                    <CheckCircle2 className="h-5 w-5 text-[#7DE0A5] flex-shrink-0" strokeWidth={2.5} />
                                ) : (
                                    <Circle className="h-5 w-5 text-white/40 flex-shrink-0" strokeWidth={2.5} />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className={`font-display font-bold ${it.done ? "text-white/60 line-through" : "text-white"}`}>
                                        {it.label}
                                    </div>
                                    <div className={`text-xs ${it.done ? "text-white/40" : "text-white/70"}`}>
                                        {it.description}
                                    </div>
                                </div>
                                {!it.done && it.cta_label && (
                                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#FF4F18]">
                                        {it.cta_label}
                                        <ChevronRight className="h-3 w-3" strokeWidth={3} />
                                    </div>
                                )}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default LaunchChecklist;
